/**
 * Cognito auth glue — thin wrapper around amazon-cognito-identity-js.
 *
 * Uses SRP auth (USER_SRP_AUTH) against the VTT app client. The pool
 * ID and client ID are baked in at build time via Vite env vars; the
 * app client must be configured as a public client (no secret) with
 * ALLOW_USER_SRP_AUTH and ALLOW_REFRESH_TOKEN_AUTH enabled.
 *
 * We expose four things the rest of the app needs:
 *   - signIn / signOut
 *   - getCurrentUser (session rehydration on page load)
 *   - getIdToken (attached to WebSocket $connect as ?token=...)
 */
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
const ClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string;

if (!UserPoolId || !ClientId) {
  // Fail loud in dev; the login page would otherwise blow up later.
  // eslint-disable-next-line no-console
  console.error(
    'Missing VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_APP_CLIENT_ID — check .env',
  );
}

export const userPool = new CognitoUserPool({
  UserPoolId,
  ClientId,
});

export interface AuthUser {
  sub: string;
  username: string;
  email: string;
  /** Cognito groups the user belongs to (from the cognito:groups claim). */
  groups: string[];
}

/** True if the user is a member of the "admins" Cognito group. */
export function isAdmin(user: AuthUser | null): boolean {
  return !!user && user.groups.includes('admins');
}

/**
 * Result of a sign-in attempt. The "newPasswordRequired" branch covers
 * the Cognito flow where an admin created the user with a temporary
 * password — the caller must collect a permanent password and call
 * <c>complete</c> to finish the challenge. The in-flight CognitoUser
 * is captured in the closure so completeNewPasswordChallenge has the
 * session state it needs.
 */
export type SignInResult =
  | { kind: 'success'; user: AuthUser }
  | {
      kind: 'newPasswordRequired';
      /** Attributes Cognito wants the user to fill in during first login. */
      requiredAttributes: string[];
      complete: (newPassword: string) => Promise<AuthUser>;
    };

function userFromSession(
  cognitoUser: CognitoUser,
  session: CognitoUserSession,
): AuthUser {
  const payload = session.getIdToken().payload as Record<string, unknown>;
  const rawGroups = payload['cognito:groups'];
  const groups = Array.isArray(rawGroups)
    ? rawGroups.map((g) => String(g))
    : typeof rawGroups === 'string' && rawGroups.length > 0
      ? [rawGroups]
      : [];
  return {
    sub: String(payload.sub ?? ''),
    username: String(
      payload['cognito:username'] ?? payload.preferred_username ?? cognitoUser.getUsername(),
    ),
    email: String(payload.email ?? ''),
    groups,
  };
}

export function signIn(username: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const details = new AuthenticationDetails({ Username: username, Password: password });

    cognitoUser.authenticateUser(details, {
      onSuccess: (session) =>
        resolve({ kind: 'success', user: userFromSession(cognitoUser, session) }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Cognito rejects these two on completeNewPasswordChallenge because
        // they aren't user-writable. Strip before we re-submit.
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;

        const complete = (newPassword: string) =>
          new Promise<AuthUser>((res, rej) => {
            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
              onSuccess: (session) => res(userFromSession(cognitoUser, session)),
              onFailure: (err) => rej(err),
            });
          });

        resolve({
          kind: 'newPasswordRequired',
          requiredAttributes: Array.isArray(requiredAttributes) ? requiredAttributes : [],
          complete,
        });
      },
    });
  });
}

export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

/**
 * Kick off the "forgot password" flow: tells Cognito to email (or SMS,
 * depending on pool config) a one-time verification code to the address
 * associated with the account. The pool must have an account recovery
 * mechanism configured or this call returns an error.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Complete the "forgot password" flow by submitting the code Cognito
 * sent plus the new password. On success the user still needs to sign
 * in again — we don't auto-log them in here to keep the flow explicit.
 */
export function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Rehydrate session from the browser's Cognito storage. Returns null if
 * there's no user cached or the refresh token has expired.
 */
export function getCurrentUser(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(userFromSession(cognitoUser, session));
    });
  });
}

/**
 * Get a currently valid ID token, refreshing silently if the cached
 * one is stale. This is what we pass to API Gateway WebSocket on
 * $connect — the Cognito authorizer validates its signature server-side.
 */
export function getIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}
